import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { SelectableCard } from '@ui/SelectableCard';
import { NumberField } from '@ui/inputs';
import { TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import { DocTypeSelect } from './DocTypeSelect';
import { implementedKindsFor, providesFor, dateProvidesFor } from './specSchema';
import { Chip, Consequence, MicroLabel, Row, Stack } from './styles';

// Question 1 — when does it run.
//
// Kind cards, not a select, because each choice carries a caveat the operator needs to read at the
// moment of choosing: `pre_earnings_release` is backfill-only until the live calendar publisher
// ships, and the two planned kinds are offered-but-disabled with the reason rather than hidden, so
// their absence is information instead of a mystery.
//
// Changing the kind does NOT silently repair the rest of the spec. It offers the repair. Silently
// clearing an anchor is how an operator ends up looking at a window they never chose.

const CHANGE_NOTE = 'Changing this changes what the document set has to work with.';

export const WhenPanel = () => {
  const { definition, setValue, removeValue, provides } = React.useContext(SpecEditorContext);

  const trigger = (definition || {}).trigger || {};
  const documents = ((definition || {}).document_set || {}).documents || {};
  const range = documents.date_range;

  const onPickKind = (kind) => {
    if (kind === trigger.kind) return;
    setValue('trigger.kind', kind);

    // Params that belong to the kind being left are removed; params the new kind needs are seeded
    // with the schema's documented defaults so the panel never renders a half-formed control.
    if (kind === 'pre_earnings_release') {
      removeValue('trigger.doc_types');
      // `== null` and not `=== undefined`: NumberField writes null for an emptied box, and a null
      // is not a legal StrictInt. Testing for undefined left a nulled field un-seeded on the way
      // back to this kind.
      if (trigger.lead_time_days == null) setValue('trigger.lead_time_days', 2);
    }
    if (kind === 'document_available') {
      removeValue('trigger.lead_time_days');
      if (trigger.doc_types === undefined) setValue('trigger.doc_types', []);
    }
  };

  const nextProvides = providesFor(trigger.kind);
  // Prefer a date-typed field: the loader only checks membership in Provides, so anchoring on a
  // company reference compiles and means nothing.
  const anchorSuggestion = dateProvidesFor(trigger.kind)[0] || nextProvides[0];
  const anchorField = range && range.anchor ? String(range.anchor).replace(/^trigger\./, '') : null;
  const anchorStranded = !!anchorField && nextProvides.indexOf(anchorField) === -1;

  const docTypes = Array.isArray(trigger.doc_types) ? trigger.doc_types : [];

  return (
    <div>
      <MicroLabel>What makes this spec fire</MicroLabel>

      <Container top={16}>
        <Stack>
          {implementedKindsFor('trigger').map((entry) => (
            <SelectableCard key={entry.kind} selected={trigger.kind === entry.kind}>
              <input
                type="radio"
                name="trigger-kind"
                checked={trigger.kind === entry.kind}
                onChange={() => onPickKind(entry.kind)}
              />
              <div>
                <Body5 bold>{entry.kind}</Body5>
                <Container top={2}>
                  <Body7 isLowContrast>{entry.blurb}</Body7>
                </Container>
                {entry.caveat ? (
                  <Container top={4}>
                    <Consequence $tone="warn">{entry.caveat}</Consequence>
                  </Container>
                ) : null}
              </div>
            </SelectableCard>
          ))}
        </Stack>
      </Container>

      {trigger.kind === 'pre_earnings_release' && (
        <Container top={16}>
          <NumberField
            label="Lead time (days before the release)"
            min={0}
            value={trigger.lead_time_days}
            onChange={(v) => setValue('trigger.lead_time_days', v)}
            hint="Several specs may use different lead times against the same release."
          />
        </Container>
      )}

      {trigger.kind === 'document_available' && (
        <Container top={16}>
          <MicroLabel>Document types that fire it</MicroLabel>
          <Container top={8}>
            {docTypes.map((type, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Container key={`trigger-doc-type-${index}`} bottom={8}>
                <Row $align="flex-start">
                  <DocTypeSelect
                    ariaLabel={`Document type ${index + 1}`}
                    value={type}
                    onChange={(v) => setValue(`trigger.doc_types.${index}`, v)}
                    invalid={!type}
                  />
                  <TransparentFlatButton onClick={() => removeValue(`trigger.doc_types.${index}`)}>
                    Remove
                  </TransparentFlatButton>
                </Row>
              </Container>
            ))}
            <TransparentFlatButton onClick={() => setValue(`trigger.doc_types.${docTypes.length}`, '')}>
              Add a document type
            </TransparentFlatButton>
          </Container>
          <Container top={8}>
            <Consequence>Type names are not checked. A typo loads cleanly and then simply never fires.</Consequence>
          </Container>
        </Container>
      )}

      <Container top={24}>
        <MicroLabel>What this trigger hands downstream</MicroLabel>
        <Container top={8}>
          <Row>
            {provides.length === 0 ? (
              <Body7 isLowContrast>Nothing — pick a trigger above.</Body7>
            ) : (
              provides.map((field) => <Chip key={field} $tone="good">{`trigger.${field}`}</Chip>)
            )}
          </Row>
        </Container>
        <Container top={8}>
          <Consequence>{CHANGE_NOTE}</Consequence>
        </Container>
      </Container>

      {anchorStranded && (
        <Container top={16}>
          <Consequence $tone="bad">
            {`The document window is anchored on ${range.anchor}, which this trigger does not provide.`}
          </Consequence>
          <Container top={6}>
            <Row>
              <TransparentFlatButton
                onClick={() => {
                  setValue('document_set.documents.date_range.from', 'now-32d');
                  setValue('document_set.documents.date_range.to', 'now-2d');
                  removeValue('document_set.documents.date_range.anchor');
                }}
              >
                Convert the window to now-32d → now-2d
              </TransparentFlatButton>
              {nextProvides.length > 0 && (
                <TransparentFlatButton
                  onClick={() => setValue('document_set.documents.date_range.anchor', `trigger.${anchorSuggestion}`)}
                >
                  {`Re-anchor on trigger.${anchorSuggestion}`}
                </TransparentFlatButton>
              )}
            </Row>
          </Container>
        </Container>
      )}
    </div>
  );
};
